import { useColleagues } from '@/shared/api/session'
import { cls } from '@/ui/cls'
import { useBrand } from '@/shared/white-label/useBrand'

interface Props {
  value: string | null
  onChange: (email: string | null) => void
  className?: string
  disabled?: boolean
  title?: string
}

/**
 * Picks the closer a lead is handed over to.
 *
 * This replaced a bare text input. Free text is why the same person accumulated
 * as "Anna", "anna", "Anna L" and "anna.l@yllw.se" — four values in the column's
 * filter dropdown, one actual human, and no way to report per closer. The API
 * now rejects a seller that isn't an active CRM user, so a text box would just
 * produce 400s.
 *
 * A legacy value that matched no user survives in the data (the migration
 * deliberately preserves it rather than discarding who a lead went to). Such a
 * value is rendered as a disabled option so the select shows the truth instead
 * of silently reading as "unassigned" — and picking anything else replaces it.
 */
export default function SellerPicker({ value, onChange, className, disabled, title }: Props) {
  const { data } = useColleagues()
  const { person, anonymised } = useBrand()
  const colleagues = data?.users ?? []

  const known = !!value && colleagues.some(c => c.email.toLowerCase() === value.toLowerCase())
  const legacy = !!value && !known

  return (
    <select
      className={cls('inline-select', className)}
      value={value ?? ''}
      disabled={disabled}
      onChange={e => onChange(e.target.value || null)}
      title={legacy ? `"${value}" är inte en CRM-användare — välj en kollega istället` : title}
    >
      <option value="">—</option>
      {legacy && (
        <option value={value!} disabled>
          {person(value)} (inte en CRM-användare)
        </option>
      )}
      {colleagues.map(c => (
        <option key={c.email} value={c.email}>
          {anonymised ? person(c.email) : c.name}
        </option>
      ))}
    </select>
  )
}
